import { useMemo, useState } from "react";
import { Area, Bar, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import { cn } from "@/lib/utils";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

/**
 * Kapasitetsutsikt – the four perspectives on one axis, week by week.
 *   physical  hairline (fleet)          hard   graphite area (contracts)
 *   soft      primary @ low alpha       weighted primary line with dots
 *   shortfall rose bars (if every offer wins)   plan  amber dashed (the plan being evaluated)
 * Blue is reserved for pipeline (a forecast), never for a status.
 */
const C = {
  physical: "hsl(var(--border))",
  hard: "hsl(var(--foreground))",
  soft: "#2670F2",
  weighted: "#2670F2",
  shortfall: "#F43F5E",
  plan: "#D97706",
  need: "#D97706",
};

const LENSES = [
  { value: "all", label: "Alle" },
  { value: "hard", label: "Hard" },
  { value: "soft", label: "Soft" },
  { value: "weighted", label: "Vektet" },
  { value: "conflict", label: "Konflikt" },
];

const Tip = ({ active, payload, label, hasPlan }) => {
  if (!active || !payload?.length) return null;
  const w = payload[0]?.payload || {};
  const rows = [
    ["Fysisk", w.physical],
    ["Kontraktsfestet", w.hard],
    ["Ledig (hard)", w.free_hard],
    ["Soft holds", w.soft],
    ["Vektet", w.weighted],
    hasPlan ? ["Denne planen", w.plan] : null,
    hasPlan ? ["Ledig etter plan", w.free_after_plan] : null,
    [hasPlan ? "Mangel etter plan" : "Mangel hvis alle vinner", hasPlan ? w.shortfall_after_plan : w.shortfall],
  ].filter(Boolean);
  return (
    <div className="rounded-lg bg-card px-3 py-2 text-[12px] shadow-pop" data-testid="capacity-outlook-tooltip">
      <div className="mb-1 font-medium">{label} <span className="text-muted-foreground">· {w.start}</span></div>
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 text-muted-foreground tabular">
          <span>{k}</span>
          <span className={cn("font-mono text-foreground", k.startsWith("Mangel") && v > 0 && "text-rose-600 dark:text-rose-400")}>{v ?? 0}</span>
        </div>
      ))}
    </div>
  );
};

export const OutlookChart = ({ weeks = [], hasPlan = false, onWeekClick, height = 240, className, compact, lens: lensProp, onLensChange, highlightRange, minChartWidth = 0 }) => {
  const [lensState, setLensState] = useState("all");
  const lens = lensProp ?? lensState;
  const setLens = onLensChange ?? setLensState;
  const data = useMemo(() => weeks.map((w) => ({ ...w, shortfallShown: hasPlan ? w.shortfall_after_plan || 0 : w.shortfall || 0, planStack: hasPlan ? w.plan || 0 : 0 })), [weeks, hasPlan]);
  const physical = data[0]?.physical || 0;
  const show = (k) => lens === "all" || lens === k || (lens === "conflict" && (k === "soft" || k === "shortfall" || k === "hard"));
  const interval = compact ? Math.max(1, Math.floor(data.length / 6)) : data.length > 30 ? 3 : data.length > 16 ? 1 : 0;

  return (
    <div className={className} data-testid="capacity-outlook-chart">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground" data-testid="capacity-outlook-legend">
          <span className="inline-flex items-center gap-1.5"><span className="h-[1px] w-3 bg-border" aria-hidden /> Fysisk {physical}</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-foreground/70" aria-hidden /> Hard</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-primary/30" aria-hidden /> Soft</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-[2px] w-3 rounded-full bg-primary" aria-hidden /> Vektet</span>
          {hasPlan && <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-amber-500/60" aria-hidden /> Denne planen</span>}
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-rose-500" aria-hidden /> Mangel</span>
        </div>
        <ToggleGroup type="single" value={lens} onValueChange={(v) => v && setLens(v)} size="sm" className="h-7 gap-0 rounded-lg bg-muted p-0.5" data-testid="capacity-outlook-perspective-toggle">
          {LENSES.map((l) => (
            <ToggleGroupItem key={l.value} value={l.value} className="h-6 rounded-md px-2 text-[11.5px] data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid={`capacity-outlook-lens-${l.value}`}>
              {l.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <div className={cn(minChartWidth > 0 && "overflow-x-auto scrollbar-thin")}>
      <div style={{ height, minWidth: minChartWidth || undefined }} className="w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} onClick={(e) => e?.activePayload?.[0] && onWeekClick?.(e.activePayload[0].payload)} barCategoryGap={2}>
            <XAxis dataKey="week" tick={{ fontSize: 10.5, fill: "#8a93a3" }} axisLine={false} tickLine={false} interval={interval} />
            <YAxis width={34} tick={{ fontSize: 10.5, fill: "#8a93a3" }} axisLine={false} tickLine={false} domain={[0, Math.max(physical, ...data.map((d) => d.hard + d.soft)) * 1.05 || "auto"]} />
            <Tooltip content={<Tip hasPlan={hasPlan} />} cursor={{ stroke: "rgba(16,24,40,0.15)" }} />
            {physical > 0 && <ReferenceLine y={physical} stroke={C.physical} strokeDasharray="3 3" label={{ value: `Fysisk ${physical}`, position: "insideTopRight", fontSize: 10, fill: "#8a93a3" }} />}
            {highlightRange && <ReferenceLine x={highlightRange[0]} stroke={C.plan} strokeOpacity={0.5} />}
            {highlightRange && <ReferenceLine x={highlightRange[1]} stroke={C.plan} strokeOpacity={0.5} />}
            {show("hard") && <Area type="stepAfter" dataKey="hard" name="Kontraktsfestet" stackId="load" stroke={C.hard} strokeOpacity={0.75} strokeWidth={1.25} fill={C.hard} fillOpacity={0.14} isAnimationActive={false} />}
            {hasPlan && show("hard") && <Area type="stepAfter" dataKey="planStack" name="Denne planen" stackId="load" stroke={C.plan} strokeDasharray="4 3" strokeWidth={1.25} fill={C.plan} fillOpacity={0.18} isAnimationActive={false} />}
            {show("soft") && <Area type="stepAfter" dataKey="soft" name="Soft holds" stackId="load" stroke={C.soft} strokeOpacity={0.5} strokeWidth={1} fill={C.soft} fillOpacity={0.12} isAnimationActive={false} />}
            {show("weighted") && <Line type="monotone" dataKey="weighted" name="Vektet" stroke={C.weighted} strokeWidth={1.5} dot={{ r: 1.5, strokeWidth: 0, fill: C.weighted }} isAnimationActive={false} />}
            {show("shortfall") && <Bar dataKey="shortfallShown" name="Mangel" fill={C.shortfall} fillOpacity={0.85} radius={[2, 2, 0, 0]} maxBarSize={18} isAnimationActive={false} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      </div>
    </div>
  );
};

/** Small horizontal-scroll variant for 390px – controls stay put, only the plot scrolls. */
export const OutlookMini = ({ weeks = [], hasPlan, className }) => <OutlookChart weeks={weeks} hasPlan={hasPlan} height={180} className={className} minChartWidth={Math.max(560, weeks.length * 24)} />;
